const { getLocationByCustomerIdQuery, getLocationByUuidQuery } = require('./queries')
const { v4 } = require('uuid')
const get = require('lodash/get')
const set = require('lodash/set')
const groupBy = require('lodash/groupBy')
const orderBy = require('lodash/orderBy')
const { transformItem: parseDepositLineItem, baseDepositData } = require('../quickbooks/tools/transform-to-deposit')
const { syncQboObjs, syncPaymentPlanBalances } = require('../finances/sync')

/**
 * @typedef {'installment' | 'invoice'} TransactionType
*/

/**
 * @typedef {Object} PaymentPlan
 * @property {number} paymentPlanId
 * @property {number} invoiceId
 * @property {number} balance
 * @property {number} installmentAmount
 * @property {number} numInstallments
 * @property {string} startDate
 * @property {string} frequency
 * @property {string} createdAt
 * @property {number} serviceFee
*/

/**
 * @typedef {Object} Invoice
 * @property {number} txnId
 * @property {string} docNo
 * @property {string} txnDate
 * @property {TransactionType} txnType
 * @property {string} dueDate
 * @property {number} amount
 * @property {number} balance
 * @property {string} currency
*/

/**
 * @typedef {Object} Installment
 * @property {string} txnId
 * @property {TransactionType} txnType
 * @property {number} paymentPlanId
 * @property {number} installmentNo
 * @property {number} amount
 * @property {number} balance
 * @property {string} txnDate
 * @property {string} dueDate
 * @property {number} serviceFee
*/

/**
 * @typedef {Object} PriorityTransaction
 * @property {TransactionType} txnType
 * @property {string} txnId
*/

class AutoReconciliation {
  /**
   * @param {import('../quickbooks')} quickbooksApi
   * @param {import('../finances')} financeApi
   * @param {import('../payment')} paymentApi
   * @param {import('../finances/wallet').Wallet} walletApi
   */
  constructor (quickbooksApi, pgConnection, financeApi, paymentApi, walletApi) {
    this.quickbooksApi = quickbooksApi
    this.pgConnection = pgConnection
    this.financeApi = financeApi
    this.paymentApi = paymentApi
    this.walletApi = walletApi
  }

  splitPaymentAmountToPayables (payables, amount) {
    let totalAmountApplied = 0
    let paymentAmount = amount
    const { paymentPlansWithFees, paymentPlansNoFees, invoices, accountIds } = payables.reduce((acc, payable) => {
      let amountToApply = 0
      for (const transaction of payable.transactions) {
        const balance = transaction.amount - transaction.amount_paid
        if (paymentAmount > (totalAmountApplied + balance)) {
          amountToApply = balance
        } else {
          const currentBalance = paymentAmount - totalAmountApplied
          amountToApply = balance > currentBalance ? currentBalance : balance
        }

        if (amountToApply > 0) {
          totalAmountApplied += amountToApply
          if (transaction.txn_type === 'installment') {
            const serviceFee = get(transaction, 'payment_plan.service_fee')
            const quickbooksAccountId = get(transaction, 'payment_plan.quickbooks_account_id')
            const paymentPlanId = get(transaction, 'payment_plan.id')
            const currentTxnNumber = get(transaction, 'txn_number')
            const separateServiceFeeAndPrincipal = get(transaction, 'payment_plan.separate_service_fee_and_principal', false)
            acc.accountIds.add(quickbooksAccountId)
            // Conditionally book service fees per payment plan if no separate interest fee invoice and service fee present
            if (serviceFee && !separateServiceFeeAndPrincipal) {
              if (!acc.paymentPlansWithFees[paymentPlanId]) {
                set(acc.paymentPlansWithFees, paymentPlanId, { ...transaction.payment_plan, amountToApply, maxTxnNumber: currentTxnNumber })
              } else {
                const currentMaxTxnNumber = get(acc.paymentPlansWithFees[paymentPlanId], 'maxTxnNumber')
                set(acc.paymentPlansWithFees[paymentPlanId], 'amountToApply', acc.paymentPlansWithFees[paymentPlanId].amountToApply + amountToApply)
                set(acc.paymentPlansWithFees[paymentPlanId], 'maxTxnNumber', currentMaxTxnNumber > currentTxnNumber ? currentMaxTxnNumber : currentTxnNumber)
              }
            } else {
              if (!acc.paymentPlansNoFees[paymentPlanId]) {
                set(acc.paymentPlansNoFees, paymentPlanId, { ...transaction.payment_plan, amountToApply })
              } else {
                set(acc.paymentPlansNoFees[paymentPlanId], 'amountToApply', acc.paymentPlansNoFees[paymentPlanId].amountToApply + amountToApply)
              }
            }
          } else {
            set(acc.invoices, transaction.txn_id, { ...transaction, amountToApply })
          }
        }
      }
      return acc
    }, {
      paymentPlansWithFees: {},
      paymentPlansNoFees: {},
      invoices: {},
      accountIds: new Set()
    })

    return { paymentPlansWithFees, paymentPlansNoFees, invoices, accountIds: [...accountIds], totalAmountApplied }
  }

  /**
   * @param {Object} payables
   * @param {PaymentPlan[]} payables.paymentPlans
   * @param {Invoice[]} payables.invoices
   * @param {Installment[]} payables.installments
   * @param {number} amount
   * @param {PriorityTransaction[]} priorityTransactions
   * @returns
   */
  splitPaymentToPayablesV2 (payables, amount, priorityTransactions) {
    // Priority transactions is an array of transactions the booking method should priorities booking for when splitting payments
    // These transactions get sorted at the top of the sorted payables list before due date and transaction date sorting
    // This is useful when reconciling payments against specific invoices or installments
    const priorities = priorityTransactions && Array.isArray(priorityTransactions) ? priorityTransactions.map(txn => `${txn.txnType}:${txn.txnId}`) : []

    // we sort the payables by priority transactions first (if any), due date second then by transaction date to ensure that
    // we always pay the oldest transactions first regardless of shared due dates
    const sortedPayables = orderBy(
      [...payables.invoices, ...payables.installments],
      [
        (transaction) => {
          const index = priorities.indexOf(`${transaction.txnType}:${transaction.txnId}`)
          return index === -1 ? Infinity : index
        },
        (transaction) => new Date(transaction.dueDate),
        (transaction) => new Date(transaction.txnDate)
      ],
      ['asc', 'asc']
    )
    const paymentPlansByAccountId = groupBy(payables.paymentPlans, 'paymentPlanId')

    let totalAmountApplied = 0
    let paymentAmount = amount
    const { paymentPlansWithFees, paymentPlansNoFees, invoices, accountIds } = sortedPayables.reduce((acc, transaction) => {
      let amountToApply = 0
      const balance = transaction.balance

      if (paymentAmount > (totalAmountApplied + balance)) {
        amountToApply = balance
      } else {
        const currentBalance = paymentAmount - totalAmountApplied
        amountToApply = balance > currentBalance ? currentBalance : balance
      }

      if (amountToApply > 0) {
        totalAmountApplied += amountToApply
        if (transaction.txnType === 'installment') {
          const serviceFee = get(transaction, 'serviceFee')
          const paymentPlanId = get(transaction, 'paymentPlanId')
          const currentTxnNumber = get(transaction, 'installmentNo')
          const separateServiceFeeAndPrincipal = get(transaction, 'separateServiceFeeAndPrincipal', false)
          acc.accountIds.add(paymentPlanId)
          // Conditionally book service fees per payment plan if no separate interest fee invoice and service fee present
          if (serviceFee && !separateServiceFeeAndPrincipal) {
            if (!acc.paymentPlansWithFees[paymentPlanId]) {
              set(acc.paymentPlansWithFees, paymentPlanId, { ...paymentPlansByAccountId[paymentPlanId][0], amountToApply, maxTxnNumber: currentTxnNumber })
            } else {
              const currentMaxTxnNumber = get(acc.paymentPlansWithFees[paymentPlanId], 'maxTxnNumber')
              set(acc.paymentPlansWithFees[paymentPlanId], 'amountToApply', acc.paymentPlansWithFees[paymentPlanId].amountToApply + amountToApply)
              set(acc.paymentPlansWithFees[paymentPlanId], 'maxTxnNumber', currentMaxTxnNumber > currentTxnNumber ? currentMaxTxnNumber : currentTxnNumber)
            }
          } else {
            if (!acc.paymentPlansNoFees[paymentPlanId]) {
              set(acc.paymentPlansNoFees, paymentPlanId, { ...paymentPlansByAccountId[paymentPlanId][0], amountToApply })
            } else {
              set(acc.paymentPlansNoFees[paymentPlanId], 'amountToApply', acc.paymentPlansNoFees[paymentPlanId].amountToApply + amountToApply)
            }
          }
        } else {
          set(acc.invoices, transaction.txnId, { ...transaction, amountToApply })
        }
      }
      return acc
    }, {
      paymentPlansWithFees: {},
      paymentPlansNoFees: {},
      invoices: {},
      accountIds: new Set()
    })

    return { paymentPlansWithFees, paymentPlansNoFees, invoices, accountIds: [...accountIds], totalAmountApplied }
  }

  /**
   * @param {Object} param
   * @param {string} param.reference
   * @param {string} param.currency
   * @param {boolean} useWalletApi
   */
  async reconcilePaystackPayment ({ reference, currency }, useWalletApi) {
    const error = new Error()
    const companyCode = this.quickbooksApi.CURRENCY_TO_COMPANY_CODE[currency]

    if (!companyCode) {
      error.message = `Country code not found for paystack transaction`
      error.status = 404
      throw error
    }

    const { transaction: paymentTransaction, paymentMethod } = await this.paymentApi.validatePayment(
      reference,
      'paystack',
      companyCode
    )

    const { metadata, status, amount: amountInCents, customer: paystackCustomer, created_at: startDate } = paymentTransaction
    const amount = amountInCents / 100

    if (status !== 'success') {
      error.message = 'Invalid transaction status'
      error.status = 400
      throw error
    }

    const locationUuid = metadata && metadata.locationUuid
    const { query, values, queryName } = locationUuid
      ? getLocationByUuidQuery(locationUuid)
      : getLocationByCustomerIdQuery(`paystack_${companyCode}`.toUpperCase(), paystackCustomer.id)
    const { rows } = await this.pgConnection.query(query, values)

    if (!rows.length) {
      error.message = 'No location linked to paystack payment'
      error.queryName = queryName
      error.status = 404
      throw error
    }

    if (rows.length > 1) {
      error.message = 'Multiple locations linked to the same paystack payment customer'
      error.status = 500
      error.queryName = queryName
      error.locations = rows
      throw error
    }

    const location = rows[0]
    const existingDeposit = await this.quickbooksApi.getExistingDeposit({
      startDate,
      reference,
      companyCode: location.company_code,
      customerId: location.customer_id
    })

    if (existingDeposit) {
      error.message = 'Payment already booked as a deposit'
      error.status = 400
      error.code = '01'
      throw error
    }

    let payables
    if (useWalletApi) {
      payables = await this.walletApi.getPayables(location.location_id)
    } else {
      const { results } = await this.financeApi.transactions.getPayablesList({
        locationId: location.location_id,
        includeTxnInfo: true,
        excludeOrderId: false
      })
      payables = results
    }

    const {
      paystackDepositAccountId,
      paymentPlanFeesAccountId,
      accountReceivableId
    } = this.quickbooksApi.quickbooksInterface.accountIds[location.company_code]
    const allAccountIds = new Set([paystackDepositAccountId, paymentPlanFeesAccountId, accountReceivableId])
    const lines = []
    const priorityTransactions = get(metadata, 'priorityTransactions', [])

    const {
      paymentPlansWithFees,
      paymentPlansNoFees,
      invoices,
      accountIds,
      totalAmountApplied
    } = useWalletApi ? this.splitPaymentToPayablesV2(payables, amount, priorityTransactions) : this.splitPaymentAmountToPayables(payables, amount)
    accountIds.forEach(id => allAccountIds.add(id))

    if (totalAmountApplied > amount) {
      error.message = 'Amount applied on transactions greater than paystack payment amount'
      error.status = 500
      throw error
    }

    const [customer, accounts, serviceFeeDeposits, singleLRBookingServiceFeeDeposits] = await Promise.all([
      this.quickbooksApi.entities.customer.get(location.company_code, location.customer_id),
      this.quickbooksApi.entities.account.getList([...allAccountIds], location.company_code),
      useWalletApi ? this.walletApi.getDepositLines([paymentPlanFeesAccountId], location.company_code, location.customer_id) : this.quickbooksApi.rawTransactions.getLoanAccountFeeDeposits(accountIds, location.company_code),
      this.walletApi.getLRSingleBookingFeeLines(accountIds, paymentPlanFeesAccountId, location.company_code, location.customer_id)
    ])

    if (accounts.length < allAccountIds.size) {
      error.message = 'Account not found'
      error.status = 404
      error.accounts = { accountIds: [...allAccountIds], response: accounts.map(a => a.Id) }
      throw error
    }

    const accountsById = groupBy(accounts, 'Id')
    const serviceFeeDepositsByAccountId = groupBy(serviceFeeDeposits, 'accountId')
    const singleLRFeeDepositByAccountId = groupBy(singleLRBookingServiceFeeDeposits, 'accountId')
    // Book extra amounts to the clients A/R
    const paymentBalance = amount - totalAmountApplied
    if (paymentBalance > 0) {
      lines.push(
        parseDepositLineItem({
          qboCustomer: customer,
          account: accountsById[accountReceivableId][0],
          amount: paymentBalance,
          description: reference,
          quickbooksInvoice: null,
          classRef: null
        })
      )
    }

    for (const transaction of Object.values(paymentPlansWithFees)) {
      const accountId = useWalletApi ? get(transaction, 'paymentPlanId') : get(transaction, 'quickbooks_account_id')
      const instalmentTerms = useWalletApi ? transaction.numInstallments : transaction.term
      const serviceFee = useWalletApi ? transaction.serviceFee : transaction.service_fee
      const totalServiceFee = instalmentTerms * serviceFee
      const serviceFeesPaid = [...get(serviceFeeDepositsByAccountId, paymentPlanFeesAccountId, []), ...get(singleLRFeeDepositByAccountId, paymentPlanFeesAccountId, [])]
        .filter(f => f.description === `LR:${accountId}`)
        .reduce((sum, fee) => sum + fee.amount, 0)
      const serviceFeesPaidCount = instalmentTerms - Math.round((totalServiceFee - serviceFeesPaid) / serviceFee)
      let serviceFeeToApply = 0

      if (transaction.maxTxnNumber > serviceFeesPaidCount) {
        const transactionServiceFees = (transaction.maxTxnNumber - serviceFeesPaidCount) * serviceFee
        const feesPaidPlusTransactionFees = serviceFeesPaid + transactionServiceFees
        if (feesPaidPlusTransactionFees > totalServiceFee) {
          serviceFeeToApply = totalServiceFee > serviceFeesPaid ? totalServiceFee - serviceFeesPaid : 0
        } else {
          serviceFeeToApply = transactionServiceFees
        }
      }
      const amountToApply = transaction.amountToApply - serviceFeeToApply

      if (serviceFeeToApply) {
        lines.push(
          parseDepositLineItem({
            qboCustomer: customer,
            account: accountsById[paymentPlanFeesAccountId][0],
            amount: serviceFeeToApply,
            description: `LR:${accountId}`,
            quickbooksInvoice: null,
            classRef: null
          })
        )
      }

      if (amountToApply) {
        lines.push(
          parseDepositLineItem({
            qboCustomer: customer,
            account: accountsById[accountId][0],
            amount: amountToApply,
            description: reference,
            quickbooksInvoice: null,
            classRef: null
          })
        )
      }
    }

    for (const transaction of Object.values(paymentPlansNoFees)) {
      const accountId = useWalletApi ? get(transaction, 'paymentPlanId') : get(transaction, 'quickbooks_account_id')
      lines.push(
        parseDepositLineItem({
          qboCustomer: customer,
          account: accountsById[accountId][0],
          amount: transaction.amountToApply,
          description: reference,
          quickbooksInvoice: null,
          classRef: null
        })
      )
    }

    const paymentRequest = {
      payment: {
        totalAmount: 0,
        companyCode: location.company_code,
        customerId: customer.Id
      },
      lines: [],
      privateNote: reference
    }

    const invoiceTxnIds = []
    for (const transaction of Object.values(invoices)) {
      const txnId = useWalletApi ? transaction.txnId : transaction.txn_id
      invoiceTxnIds.push(txnId)
      const amount = paymentRequest.payment.totalAmount + transaction.amountToApply
      set(paymentRequest.payment, 'totalAmount', amount)
      paymentRequest.lines.push({
        type: 'Invoice',
        id: txnId,
        amount: transaction.amountToApply
      })
    }

    let quickbooksPayment
    const paymentTxnIds = []
    if (paymentRequest.payment.totalAmount > 0) {
      quickbooksPayment = await this.quickbooksApi.entities.payment.create(paymentRequest)
      paymentTxnIds.push(quickbooksPayment.Id)
    }

    if (quickbooksPayment) {
      lines.push({
        Amount: quickbooksPayment.TotalAmt,
        LinkedTxn: [{
          TxnLineId: '0',
          TxnId: quickbooksPayment.Id,
          TxnType: 'Payment'
        }]
      })
    }

    const depositRequest = {
      ...baseDepositData({
        qboCustomer: customer,
        depositAccount: accountsById[paystackDepositAccountId][0],
        privateNote: reference,
        quickbooksInvoice: null
      }),
      Line: lines
    }

    const { Deposit } = await this.quickbooksApi.quickbooksInterface.post(location.company_code, 'deposit', depositRequest)

    // Fetch all transactions booked for transform
    const [updatedAccounts, updatedInvoices] = await Promise.all([
      this.quickbooksApi.entities.account.getList(accountIds, location.company_code),
      this.quickbooksApi.entities.invoice.getList(invoiceTxnIds, location.company_code)
    ])
    let updatedPayment = []
    if (quickbooksPayment) {
      const updatedQboPayment = await this.quickbooksApi.entities.payment.get(location.company_code, quickbooksPayment.Id)
      updatedPayment.push(updatedQboPayment)
    }

    if (updatedAccounts.length) {
      const accountRows = updatedAccounts.map(a => ({
        origin: 'qbo',
        company: `${companyCode}`.toUpperCase(),
        accountId: a.Id,
        balance: a.CurrentBalance
      }))
      await syncPaymentPlanBalances(this.pgConnection, accountRows)
    }

    const paygPaymentData = await this.paymentApi.paygCreditPayment.getPaystackPaygPayment(paymentTransaction.id)
    if (!paygPaymentData.length) {
      await this.paymentApi.paygCreditPayment.savePayment({
        id: v4(),
        location_id: location.location_id,
        payment_provider: `PAYSTACK_${companyCode}`.toUpperCase(),
        payment_method: paymentMethod,
        currency: paymentTransaction.currency,
        amount_in_cents: paymentTransaction.amount,
        created_by: paystackCustomer.email,
        payment_data: {
          trans: `${paymentTransaction.id}`,
          status: paymentTransaction.status,
          trxref: reference,
          message: paymentTransaction.gateway_response,
          reference: reference,
          redirecturl: `?trxref=${reference}&reference=${reference}`,
          transaction: `${paymentTransaction.id}`
        }
      })
    }

    await Promise.all([
      syncQboObjs(
        this.pgConnection,
        location.company_code,
        { account: updatedAccounts, invoice: updatedInvoices, payment: updatedPayment, deposit: [Deposit] },
        this.quickbooksApi.entities
      ),
      this.quickbooksApi.transform.transformAndSave(
        location.company_code,
        { Account: updatedAccounts, Invoice: updatedInvoices, Payment: updatedPayment, Deposit: [Deposit] }
      )
    ])
    return {
      deposit: Deposit,
      country: companyCode,
      locationFsid: location.fsid,
      reference: Deposit.PrivateNote,
      amount: Deposit.TotalAmt
    }
  }
}

module.exports = AutoReconciliation
